/**
 * Zwei-Spieler-Modus „versus“: zwei gleichberechtigte Spieler (Host und Gast) mit je einer
 * selbst gegründeten Partei. Nach jeder Wahl besetzt die Partei mit dem Regierungsauftrag den
 * Kanzler-Platz „YOU“ der Engine: ein Spieler oder eine Computerpartei (siehe slots.ts).
 * Spieler ohne Kanzleramt sind Koalitionspartner oder Opposition.
 *
 * Wer einen Zug macht, steht im Zug selbst (`as`, von der Sync-Schicht aus dem Absender
 * abgeleitet) und wird vor dem Ausführen mit setActor() gesetzt. Die Engine prüft hier nur den
 * Spielzustand und ignoriert ungültige Züge still, damit alle Clients identisch bleiben.
 */
import { NEED } from '../content/consts';
import { isHex, colorProblem, colorClash } from './colors';
import { ISSUES, TPL } from '../content/issues';
import { OUTLETS, PARTIES, CUSTOM_COLORS, syncRegistry } from '../content/parties';
import { AX, AXN, WEIGHT } from '../content/axes';
import { since } from './version';
import { zgMax } from './zeitgeist';
import { STATES, pickGov } from '../content/states';
import { PREF, RID } from '../content/ressorts';
import { pub } from './opinion';
import { on } from '../content/settings';
import { approval, poll } from './govern';
import { press, oppParties } from './media';
import { rnd } from './rng';
import { PA, PN, scoreRaw } from './rules';
import { S, log } from './state';
import { vrOf } from './court';
import { clamp } from './util';
import type { PartyId } from './types';
import { axes, allAxes } from './axes';
import { issuesNow } from './axes';

export const AP_MAX = 3;
export type Role = 'host' | 'guest';
export const ROLES: Role[] = ['host', 'guest'];
export const PID: Record<Role, string> = { host: 'P1', guest: 'P2' };

export interface CustomParty { n: string; a: string; c: string; base: number; prag: number; pos: Record<string, number>; w: Record<string, number>; custom: true }
export interface PCamp {
  step: 'themen' | 'tour' | 'werbung' | 'duell' | 'fazit';
  fund?: import('./ads').Funding;
  ads?: import('./ads').Alloc;
  opts: { id: string; n: string; sev?: number; ax?: string }[];
  themes: string[];
  stops: Record<string, number>;
  ans: { i: number; v: number; txt: string }[];
  done: boolean;
  tot?: number;
}
export interface Player {
  role: Role;
  /** Kennung der Partei im Bundestag, solange der Spieler nicht Kanzler ist. */
  pid: string;
  party: CustomParty | null;
  role2: 'opposition' | 'partner' | 'gov';
  ap: number;
  offer: null | 'pending' | 'accepted' | 'declined';
  contractOk: boolean;
  blockM: number;
  /** Rückenwind in Umfragen, fließt ins Wahlergebnis. */
  bonus: number;
  stats: { bills: number; billsOk: number; suits: number; suitsWon: number; attacks: number; misstrauen: number };
  wish: { pos: Record<string, number>; ress: string[] } | null;
  camp: PCamp | null;
  /** Ansehen aus eigener Regierungsarbeit (Koalitionspartner), klingt langsam ab. */
  credit?: number;
  /** Verschobene Programmstufen vor diesem Wahlkampf. */
  wende?: number;
  /** Koalitionspartner: Problem, zu dem gerade ein Entwurf vorbereitet wird (für den Kanzler sichtbar). */
  plan?: string | null;
}
export interface VersusState {
  mode: 'versus';
  v: 2;
  pl: Record<Role, Player>;
  /** Wer im Mehrspieler auf „Weiter“ geklickt hat. Erst wenn alle bereit sind, läuft die Zeit. */
  ready?: Partial<Record<Role, boolean>>;
  /** Wer besetzt den Kanzler-Platz? Ein Spieler, der Computer oder (vor der ersten Wahl) niemand. */
  chan: Role | 'ai' | null;
  /** Computerpartei auf dem Kanzler-Platz. */
  ai: string | null;
  /** Reihenfolge der Regierungsaufträge nach der Wahl und aktueller Index. */
  mandate: string[];
  mi: number;
  /** Fragen des gemeinsamen TV-Duells. */
  duelQ: any[] | null;
  /** Erste Wahl der Partie? */
  first: boolean;
}

// ---- Wer handelt gerade?
let ACTOR: Role = 'host';
export const setActor = (r: Role | undefined | null) => { ACTOR = r === 'guest' ? 'guest' : 'host'; };
export const actor = (): Role => ACTOR;

export const isVersus = () => !!S && !!S.mp && S.mp.mode === 'versus';
export const pl = (r: Role): Player => S.mp!.pl[r];
export const me = (): Player | null => (isVersus() ? S.mp!.pl[ACTOR] : null);
export const otherRole = (r: Role): Role => (r === 'host' ? 'guest' : 'host');
export const isChan = (r: Role) => isVersus() && S.mp!.chan === r;
/** Kennung der Partei eines Spielers im Spiel: 'YOU', wenn er Kanzler ist. */
export const pidOf = (r: Role): PartyId => (isChan(r) ? 'YOU' : pl(r).pid);
/** Spieler, die gerade nicht Kanzler sind und eine Partei haben. */
export const nonChan = (): Player[] => (isVersus() ? ROLES.filter((r) => S.mp!.chan !== r).map(pl).filter((p) => !!p.party) : []);
export const humanPids = (): string[] => nonChan().map((p) => p.pid);
export const playerByPid = (pid: string): Player | null => nonChan().find((p) => p.pid === pid) ?? null;
/** Menschliche Koalitionspartner (nicht Kanzler). */
export const humanPartners = (): string[] => nonChan().filter((p) => S.partners.includes(p.pid)).map((p) => p.pid);
export const isHumanPartner = () => humanPartners().length > 0;
/** Wer darf die Zeit weiterschalten? Ein Spieler als Kanzler, sonst der Host. */
export const timeRole = (): Role => (isVersus() && S.mp!.chan === 'guest' ? 'guest' : 'host');

/** Kompatibilität für Ansichten, die „den anderen Spieler“ zeigen: der erste Nicht-Kanzler. */
export const oppId = (): PartyId | null => nonChan()[0]?.pid ?? null;

/** Wie gut passt ein Programm zur Stimmung im Land (0–1)? Gleiche Formel wie progFit(). */
export function fitOf(pos: Record<string, number>) { const A = axes(); return A.reduce((s: number, a: string) => s + 1 - Math.abs((pos[a] ?? 0) - pub(a)) / 4, 0) / A.length; }
/** Startwert in Umfragen für eine neue Partei, für beide Spieler gleich. */
export const customBase = (pos: Record<string, number>) => clamp(9 + (fitOf(pos) - .6) * 30, 6, 21);

const newPlayer = (role: Role): Player => ({ role, pid: PID[role], party: null, role2: 'opposition', ap: AP_MAX, offer: null, contractOk: false, blockM: -99, bonus: 0, stats: { bills: 0, billsOk: 0, suits: 0, suitsWon: 0, attacks: 0, misstrauen: 0 }, wish: null, camp: null });

export function versusStart() {
  if (S.mp && S.mp.mode === 'versus') return; // schon eingerichtet
  S.mp = { mode: 'versus', v: 2, pl: { host: newPlayer('host'), guest: newPlayer('guest') }, chan: null, ai: null, mandate: [], mi: 0, duelQ: null, first: true };
  S.name = '–';
  // Neue Parteien regieren noch nirgends: Länder der Startpartei neu und zufällig vergeben
  for (const id in S.gov) if (S.gov[id] === 'YOU') S.gov[id] = pickGov(STATES[id], false);
}

/** Spieler gründet seine Partei (vor der ersten Wahl). */
export function versusCreate(def: { n: string; a: string; c: string; pos: Record<string, number> }) {
  const P = me(); if (!P || P.party || S.phase !== 'program') return;
  const n = String(def?.n ?? '').trim().slice(0, 32), a = String(def?.a ?? '').trim().slice(0, 5);
  if (n.length < 2 || a.length < 1) return;
  if (Object.values<any>(PARTIES).some((X) => X.a.toLowerCase() === a.toLowerCase() || X.n.toLowerCase() === n.toLowerCase())) return;
  const c = isHex(def.c) && !colorProblem(def.c) ? def.c.toLowerCase() : CUSTOM_COLORS.find((x) => !colorClash(x)) ?? CUSTOM_COLORS[0];
  const pos: Record<string, number> = {}, w: Record<string, number> = {};
  for (const ax of allAxes()) { const v = Math.round(clamp(Number(def.pos?.[ax]) || 0, -2, 2)); pos[ax] = v; w[ax] = Math.abs(v) >= 2 ? 2 : Math.abs(v) >= 1 ? 1.3 : .8; }
  P.party = { n, a, c, base: Math.round(customBase(pos) * 10) / 10, prag: .8, pos, w, custom: true };
  syncRegistry(S.mp);
  log(`Neue Partei: ${n} (${a}) tritt zur Bundestagswahl an.`);
}

// ---- Koalitionsangebote

/** Kanzler (Mensch) nimmt eine Spielerpartei in die Sondierung auf oder heraus. */
export function versusOnPick() {
  for (const P of nonChan()) {
    const inList = S.partners.includes(P.pid);
    if (inList && P.offer !== 'accepted') P.offer = 'pending';
    if (!inList) P.offer = null;
    P.contractOk = false;
  }
}

/** Antwort eines Spielers auf ein Koalitionsangebot oder Zustimmung zum Vertrag. */
export function coalReply(ok: boolean, after?: () => void) {
  const P = me(); if (!P || isChan(P.role)) return;
  if (S.phase === 'night' && P.offer === 'pending') {
    P.offer = ok ? 'accepted' : 'declined';
    if (!ok) { S.partners = S.partners.filter((p) => p !== P.pid); log(`${PN(P.pid)} lehnt eine Koalition mit ${S.name} ab.`, 'bad'); }
    else log(`${PN(P.pid)} ist zu Koalitionsgesprächen bereit.`, 'good');
    after?.();
  } else if (S.phase === 'talks' && S.partners.includes(P.pid)) {
    P.contractOk = ok;
    log(ok ? `${PN(P.pid)} stimmt dem Koalitionsvertrag zu.` : `${PN(P.pid)} will den Koalitionsvertrag nachverhandeln.`, ok ? 'good' : 'bad');
  }
}

export const WISH_MAX = 5;
const meInTalks = (): Player | null => { const P = me(); return P && !isChan(P.role) && S.phase === 'talks' && S.partners.includes(P.pid) ? P : null; };

/** Sondierung beginnt: Forderungen der Spieler-Partner mit ihrem Programm vorbelegen. */
export function versusOnTalks() {
  for (const P of nonChan()) {
    if (!S.partners.includes(P.pid)) continue;
    P.contractOk = false;
    if (P.wish) continue;
    const X = PARTIES[P.pid];
    P.wish = { pos: Object.fromEntries(axes().map((a: string) => [a, Math.round(clamp(X.pos[a] ?? 0, -2, 2))])), ress: (PREF[P.pid] || []).filter((r: string) => RID[r]).slice(0, WISH_MAX) };
  }
}
export function p2Wish(axis: string, v: number) {
  const P = meInTalks(); if (!P || !P.wish || !axes().includes(axis)) return;
  P.wish.pos[axis] = Math.round(clamp(Number(v) || 0, -2, 2)); P.contractOk = false;
}
export function p2RessWish(rid: string) {
  const P = meInTalks(); if (!P || !P.wish || !RID[rid]) return;
  const w = P.wish.ress, i = w.indexOf(rid);
  if (i >= 0) w.splice(i, 1); else if (w.length < WISH_MAX) w.push(rid); else return;
  P.contractOk = false;
}
/** Kanzler ändert den Vertrag: Zustimmung aller Spieler-Partner verfällt. */
export function versusContractChanged() { for (const P of nonChan()) P.contractOk = false; }

/** Wie viele Forderungen eines Spieler-Partners stehen im Vertrag? */
export function wishStatus(pid?: string) {
  const P = pid ? playerByPid(pid) : nonChan().find((x) => S.partners.includes(x.pid)) ?? null;
  if (!P || !P.wish || !S.agree) return null;
  const ax = axes(), axesN = ax.filter((a: string) => S.agree[a] === P.wish!.pos[a]).length;
  const ress = P.wish.ress.filter((r) => S.ress[r] === P.pid).length;
  return { axes: axesN, axesTotal: ax.length, ress, ressTotal: P.wish.ress.length };
}

/** Darf der Kanzler in die Sondierung bzw. unterschreiben? */
export function versusTalksOk() { return nonChan().every((P) => !S.partners.includes(P.pid) || P.offer === 'accepted'); }
export function versusSignOk() { return nonChan().every((P) => !S.partners.includes(P.pid) || P.contractOk); }

/** Regierungsstart: Rollen festlegen. */
export function versusOnGovern() {
  if (!isVersus()) return;
  S.mp!.ready = {};
  for (const r of ROLES) S.mp!.pl[r].plan = null;
  for (const r of ROLES) {
    const P = pl(r);
    P.role2 = isChan(r) ? 'gov' : S.partners.includes(P.pid) ? 'partner' : 'opposition';
    P.ap = AP_MAX; P.contractOk = false; P.offer = null;
  }
}
/** Neue Legislatur: Verhandlungsstände zurücksetzen, ein Teil des Rückenwinds bleibt. */
export function versusOnNextLeg() {
  if (!isVersus()) return;
  for (const r of ROLES) { const P = pl(r); P.offer = null; P.contractOk = false; P.wish = null; P.bonus = Math.round(P.bonus * 30) / 100; }
}

function spend(P: Player, n: number) { if (P.ap < n) return false; P.ap -= n; return true; }

/** Monatlich: Aktionspunkte und Rückenwind der Spieler ohne Kanzleramt. */
export function versusTick() {
  if (!isVersus()) return;
  const a = approval();
  for (const P of nonChan()) {
    if (P.role2 === 'opposition') {
      P.ap = Math.min(AP_MAX, P.ap + 1);
      if (a < 42) P.bonus = clamp(P.bonus + (42 - a) * .004, -3, 8);
    } else if (P.role2 === 'partner') {
      P.credit = (P.credit || 0) * .985;
      P.bonus = clamp(P.bonus + ((a - 45) * .08 + P.credit - P.bonus) * .1, -3, 8);
    }
  }
}

// ---- Abstimmungen über Regierungsgesetze

/** Angekündigte Stimmen der Spielerparteien zu einem Gesetz. */
export function humanVotes(L: any): { id: PartyId; y: boolean }[] {
  if (!L || !L.hv) return [];
  return Object.entries<boolean>(L.hv).filter(([id]) => S.res.inP.includes(id) && humanPids().includes(id)).map(([id, y]) => ({ id, y: !!y }));
}
export function p2Vote(lawId: number, yes: boolean) {
  const P = me(); if (!P || isChan(P.role) || S.phase !== 'govern' || !S.res.inP.includes(P.pid)) return;
  const L = S.laws.find((x) => x.id === lawId); if (!L || L.bestand || !['draft', 'ready', 'cabinet', 'bt'].includes(L.stage)) return;
  const hv = (L.hv as Record<string, boolean>) || (L.hv = {});
  if (hv[P.pid] === !!yes) return;
  const first = hv[P.pid] == null; hv[P.pid] = !!yes;
  log(`${PN(P.pid)} ${first ? 'kündigt an' : 'ändert ihre Haltung und kündigt an'}, „${L.t}“ ${yes ? 'zuzustimmen' : 'abzulehnen'}.`, yes ? 'good' : P.role2 === 'partner' ? 'bad' : '');
}
/** Folgen der Abstimmung: entscheidende Stimmen bringen der Opposition Rückenwind. */
export function versusOnLawVote(L: any, v: { yes: number; by: Record<string, { y: boolean }> }, passed: boolean) {
  for (const h of humanVotes(L)) {
    const P = playerByPid(h.id as string); if (!P || P.role2 !== 'opposition') continue;
    const seats = S.res.seats[h.id] || 0, need = L.fx?.gg ? 420 : NEED;
    const decisive = h.y ? passed && v.yes - seats < need : !passed && v.yes + seats >= need;
    if (h.y && decisive) { P.bonus = clamp(P.bonus + .4, -3, 8); log(`Mit den Stimmen von ${PN(h.id)} kommt „${L.t}“ durch.`, 'good'); press(OUTLETS[0].n, `${PA(h.id)} verhilft Regierung zur Mehrheit für „${L.t}“`, .2); }
    else if (!h.y && decisive) { P.bonus = clamp(P.bonus + .5, -3, 8); press(OUTLETS[0].n, `${PA(h.id)} bringt „${L.t}“ zu Fall`, -.5); }
  }
}

// ---- Opposition

const meOpp = (): Player | null => { const P = me(); return P && !isChan(P.role) && P.role2 === 'opposition' && S.phase === 'govern' ? P : null; };
export const isHumanOpp = () => nonChan().some((P) => P.role2 === 'opposition');

export function oppPropose(iss: string, tpl: number, p: Record<string, number>) {
  const P = meOpp(); if (!P || S.oppBill) return;
  const I = issuesNow().find((i: any) => i.id === iss); if (!I) return;
  const st = S.issues[iss]; if (!st || !st.on) return;
  const T = TPL(iss, tpl || 0); if (!T || T.custom) return;
  if (!spend(P, 2)) return;
  const q: Record<string, number> = Object.fromEntries(T.ps.map((x: any) => [x.id, p && p[x.id] != null ? p[x.id] : x.d]));
  const fx = T.fx(q);
  S.oppBill = { pid: P.pid, iss, tpl: tpl || 0, t: T.t, r: T.r, br: T.br, p: q, fx, m: S.m, human: true };
  P.stats.bills++;
  log(`${PN(P.pid)} bringt einen Antrag ein: „${T.t}“. Die Regierung muss sich positionieren.`);
  const o = OUTLETS.map((o: any) => ({ o, s: scoreRaw(o.pos, PARTIES[P.pid].w, fx.pos) })).sort((x: any, y: any) => y.s - x.s)[0];
  press(o.o.n, `${PA(P.pid)} legt eigenen Plan vor: „${T.t}“`, 0);
}
/** Aus decideOpp: Wie ist ein Spieler-Antrag ausgegangen? */
export function versusOnBill(pid: string, accepted: boolean, passed: boolean) {
  const P = playerByPid(pid); if (!P) return;
  if (accepted && passed) { P.stats.billsOk++; P.bonus = clamp(P.bonus + .6, -3, 8); }
  else if (!accepted) P.bonus = clamp(P.bonus + .15, -3, 8);
}
export function oppSue(lawId: number) {
  const P = meOpp(); if (!P || !on('court')) return;
  const L = S.laws.find((x) => x.id === lawId); if (!L || !['kraft', 'done'].includes(L.stage) || L.bestand || L.rep) return;
  if ((S.suits || []).some((x: any) => x.lid === L.id)) return;
  const T = TPL(L.iss!, L.tpl || 0), v = vrOf(T, L.p);
  if (!spend(P, 1)) return;
  P.stats.suits++;
  if (v < .05) { log(`Karlsruhe nimmt die Klage von ${PN(P.pid)} gegen „${L.t}“ nicht an: offensichtlich unbegründet.`); P.bonus = clamp(P.bonus - .3, -3, 8); return; }
  (S.suits = S.suits || []).push({ lid: L.id, t: L.t, by: P.pid, m: S.m + 8 + Math.floor(rnd() * 6), v, human: true });
  log(`${PN(P.pid)} klagt in Karlsruhe gegen „${L.t}“.`, 'bad'); press(OUTLETS[1].n, `Verfassungsklage gegen „${L.t}“`, -.4);
}
export function versusOnSuit(by: string, won: boolean) { const P = playerByPid(by); if (P && won) { P.stats.suitsWon++; P.bonus = clamp(P.bonus + 1, -3, 8); } }
export function oppAttack(iss: string) {
  const P = meOpp(); if (!P) return;
  const I = issuesNow().find((i: any) => i.id === iss), st = S.issues[iss]; if (!I || !st || !st.on) return;
  if (!spend(P, 1)) return;
  P.stats.attacks++;
  if (st.sev > 40) {
    st.sev = clamp(st.sev + 2, 0, 100); S.basis -= .3; P.bonus = clamp(P.bonus + .2, -3, 8);
    log(`Aktuelle Stunde: ${PN(P.pid)} wirft der Regierung Versagen beim Thema ${I.n} vor.`, 'bad');
    press(OUTLETS[3].n, `${PA(P.pid)} attackiert Regierung: „Beim Thema ${I.n} passiert nichts“`, -.7);
  } else {
    P.bonus = clamp(P.bonus - .15, -3, 8);
    log(`${PN(P.pid)} attackiert die Regierung beim Thema ${I.n}, doch das Thema zieht nicht.`);
  }
}
export function oppMisstrauen() {
  const P = meOpp(); if (!P) return;
  const bloc = oppParties().filter((id) => id !== 'HO'), seats = bloc.reduce((s, id) => s + S.res.seats[id], 0);
  if (!spend(P, 2)) return;
  P.stats.misstrauen++;
  const a = approval(), pOk = seats >= NEED ? clamp((45 - a) / 20, .05, .9) : 0;
  if (rnd() < pOk) {
    S.end = 'misstrauen'; S.endBy = bloc; S.phase = 'end';
    log(`Konstruktives Misstrauensvotum auf Antrag von ${PN(P.pid)}: Die Regierung ist gestürzt. Es gibt Neuwahlen.`, 'bad');
    press(OUTLETS[0].n, 'Regierung gestürzt: Opposition setzt Misstrauensvotum durch', -2);
  } else {
    P.bonus = clamp(P.bonus - .5, -3, 8); S.basis += .2;
    log(seats >= NEED ? `Misstrauensantrag von ${PN(P.pid)} scheitert: Die Opposition findet keine gemeinsame Mehrheit.` : `Misstrauensantrag von ${PN(P.pid)} scheitert: Die Opposition hat keine Mehrheit.`);
    press(OUTLETS[0].n, 'Misstrauensvotum gescheitert', .3);
  }
}

// ---- Version 12: weitere Werkzeuge der Opposition (wie im Einzelspieler)

/** Talkshow: für eine eigene Position werben. Passt sie zur Stimmung, gibt es Rückenwind, sonst verschiebt sie die Stimmung ein Stück. */
export function oppTalk(ax: string) {
  const P = meOpp(); if (!P || !since(12) || !P.party || !axes().includes(ax) || !spend(P, 1)) return;
  const y = P.party.pos[ax] ?? 0, p = pub(ax), fit = 1 - Math.abs(y - p) / 4;
  P.bonus = clamp(P.bonus + .1 + fit * .3, -3, 8);
  if (y !== p) { const z = (S.zg ??= {}); z[ax] = clamp((z[ax] || 0) + Math.sign(y - p) * .06, -zgMax(), zgMax()); }
  log(`Talkshow: ${PN(P.pid)} wirbt für ihre Haltung zum Thema ${AXN[ax] || ax}.`);
}
/** Landeswahlkampf: stärkt die eigene Partei bei den nächsten Landtagswahlen in diesem Land. */
export function oppLand(id: string) {
  const P = meOpp(); if (!P || !since(12) || !STATES[id] || !spend(P, 1)) return;
  const o = ((P as any).stOff ??= {}); o[id] = clamp((o[id] || 0) + 2, 0, 8);
  log(`${PN(P.pid)} macht Wahlkampf in ${STATES[id].n}.`);
}
/** Untersuchungsausschuss zu einer Affäre der letzten sechs Monate. */
export const uaReady = (): boolean => since(12) && (S as any).scM != null && S.m - (S as any).scM <= 6 && (S as any).uaM !== (S as any).scM;
export function oppUA() {
  const P = meOpp(); if (!P || !uaReady() || !spend(P, 2)) return;
  (S as any).uaM = (S as any).scM; S.basis -= .8; P.bonus = clamp(P.bonus + .8, -3, 8);
  log(`${PN(P.pid)} setzt einen Untersuchungsausschuss zur jüngsten Affäre im Kabinett durch.`, 'bad');
  press(OUTLETS[3].n, `Untersuchungsausschuss: ${PA(P.pid)} nimmt die Regierung ins Kreuzverhör`, -1);
}

// ---- Koalitionspartner

const mePartner = (): Player | null => { const P = me(); return P && !isChan(P.role) && P.role2 === 'partner' && S.partners.includes(P.pid) && S.phase === 'govern' ? P : null; };
export function partnerBlock(lawId: number) {
  const P = mePartner(); if (!P || S.m - P.blockM < 6) return;
  const L = S.laws.find((x) => x.id === lawId); if (!L || !['draft', 'ready'].includes(L.stage)) return;
  P.blockM = S.m; L.stage = 'blocked'; L.why = `${PN(P.pid)} legt als Koalitionspartner ein Veto ein.`;
  S.coal = clamp(S.coal - 4, 0, 100);
  log(`Koalitionsstreit: ${PN(P.pid)} stoppt „${L.t}“.`, 'bad'); press(OUTLETS[0].n, `Koalitionsstreit: „${L.t}“ gestoppt`, -1);
}
export function partnerQuit() {
  const P = mePartner(); if (!P) return;
  log(`${PN(P.pid)} verlässt die Koalition. Die Regierung hat keine Mehrheit mehr.`, 'bad');
  press(OUTLETS[0].n, `${PA(P.pid)} steigt aus: Koalition zerbrochen`, -2);
  S.end = 'coal'; S.phase = 'end';
}

/** Umfragewert einer Spielerpartei ohne Kanzleramt (Basis plus Rückenwind). */
export function oppPoll(pid?: string): number {
  const P = pid ? playerByPid(pid) : nonChan()[0]; if (!P || !P.party) return 0;
  return clamp(P.party.base + P.bonus, 2, 60);
}

// ---- Eigene Gesetze des Koalitionspartners

/** Ein Gesetz des menschlichen Koalitionspartners tritt in Kraft: Wirkung auf sein Ansehen. */
export function versusOnEnact(L: { by?: string; t: string; fx: { pos: Record<string, number>; g?: Record<string, number> } }) {
  if (!isVersus() || !L.by || L.by === 'YOU') return;
  const P = playerByPid(L.by); if (!P || P.role2 !== 'partner') return;
  const fit = scoreRaw(P.party!.pos, P.party!.w, L.fx.pos);
  const pop = Object.keys(WEIGHT).reduce((s, g) => s + (L.fx.g?.[g] || 0) * WEIGHT[g], 0);
  let d = clamp(pop * .25, -.8, .8);
  if (fit >= .75) d += .5; else if (fit < .45) { d -= 1; log(`Die Basis von ${PN(P.pid)} sieht „${L.t}“ als Bruch mit dem eigenen Programm.`, 'bad'); }
  P.credit = clamp((P.credit || 0) + d, -4, 6);
  if (d >= .4) { log(`„${L.t}“ wird ${PN(P.pid)} als Erfolg angerechnet.`, 'good'); press(OUTLETS[0].n, `${PA(P.pid)} setzt „${L.t}“ durch`, .2); }
  else if (d <= -.4) press(OUTLETS[1].n, `„${L.t}“: Kritik an ${PA(P.pid)}`, -.2);
}

// ---- Landtagswahlen: auch Spielerparteien ohne Kanzleramt treten an

/** Regionale Stärke einer Spielerpartei je Land (−5 … +5), einmal pro Partei ausgewürfelt. */
function regOf(pid: string, id: string): number {
  const M = S.mp as VersusState & { reg?: Record<string, Record<string, number>> };
  const reg = (M.reg ??= {});
  if (!reg[pid]) { reg[pid] = {}; for (const k of Object.keys(STATES)) reg[pid][k] = Math.round((rnd() * 10 - 5) * 10) / 10; }
  return reg[pid][id] ?? 0;
}
/** Ergebnis einer Spielerpartei bei einer Landtagswahl. Opposition profitiert vom Bundestrend gegen die Regierung. */
export function ltwShare(P: Player, id: string, moodDiff: number, incumbent: boolean): number {
  const opp = P.role2 === 'opposition';
  return clamp(oppPoll(P.pid) + (opp ? 2 : 0) + moodDiff * (opp ? -.5 : .4) + regOf(P.pid, id) + (incumbent ? 3 : 0) + (rnd() * 4 - 2) + ((P as any).stOff?.[id] || 0) * .8, 2, 60); // Version 12: Landeswahlkampf
}
/** Mehrspieler-Landtagswahl: Kanzlerpartei, Spielerparteien und die stärkste Computerpartei im Land. Die stärkste Partei regiert. */
export function ltwVersus(id: string, old: string, moodDiff: number): { gov: string; list: { id: string; s: number }[] } {
  const st = STATES[id], M = S.mp!;
  const list: { id: string; s: number }[] = [];
  // Kanzlerpartei: Umfrage minus üblicher Dämpfer für die Bundesregierung in Landtagswahlen
  list.push({ id: 'YOU', s: clamp(poll() + moodDiff * .8 - 1 + (old === 'YOU' ? 3 : 0) + (rnd() * 4 - 2), 3, 60) });
  for (const P of nonChan()) if (P.party) list.push({ id: P.pid, s: ltwShare(P, id, moodDiff, old === P.pid) });
  // Stärkste Computerpartei im Land: der bisherige Regierungschef oder die traditionelle Alternative
  const human = (x: string) => x === 'YOU' || humanPids().includes(x);
  let rival = human(old) ? st.alt : old;
  if (rival === M.ai || human(rival) || !PARTIES[rival]) rival = ['BU', 'PdA', 'OA'].find((x) => x !== M.ai && PARTIES[x]) ?? 'BU';
  const base = S.res?.share?.[rival] ?? PARTIES[rival].base ?? 15;
  list.push({ id: rival, s: clamp(base + 1 + (old === rival ? 3 : 0) + (rnd() * 6 - 3), 3, 60) });
  list.sort((a, b) => b.s - a.s);
  let gov = list[0].id;
  if (gov === M.ai) gov = 'YOU';
  for (const P of nonChan()) {
    if (gov === P.pid && old !== P.pid) P.bonus = clamp(P.bonus + .5, -3, 8);
    else if (old === P.pid && gov !== P.pid) P.bonus = clamp(P.bonus - .5, -3, 8);
  }
  for (const x of list) x.s = Math.round(x.s * 10) / 10;
  return { gov, list };
}
/** Landesregierungen einer Partei und ihre Stimmen im Bundesrat. */
export function statesOf(pid: string): { n: number; v: number } {
  let n = 0, v = 0; for (const id in STATES) if (S.gov[id] === pid) { n++; v += STATES[id].v; } return { n, v };
}

/** Koalitionspartner meldet, an welchem Problem er gerade einen Entwurf vorbereitet (null = fertig/abgebrochen). */
export function p2Plan(iss: string | null) {
  const P = me(); if (!P || P.role2 !== 'partner' || S.phase !== 'govern') return;
  P.plan = iss && S.issues[iss]?.on && !S.issues[iss].law ? iss : null;
}
/** Laufende Planungen der Koalitionspartner (Spieler). */
export const partnerPlans = (): { pid: string; iss: string }[] => (isVersus() ? nonChan().filter((P) => P.role2 === 'partner' && P.plan).map((P) => ({ pid: P.pid, iss: P.plan! })) : []);
